// M323 (2026-05-30) — Popup thông báo cho NV.
// Hiện ở góc phải dưới màn hình khi NV (kiosk) đăng nhập, tự tắt sau durationSeconds.
// Chỉ hiện 1 lần / phiên trình duyệt cho mỗi bản thông báo (version), tránh làm phiền.
import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '../lib/auth.js';
import { api } from '../lib/api.js';

export default function AnnouncementPopup() {
  const accessToken = useAuthStore(s => s.accessToken);
  const role = useAuthStore(s => s.user?.role);
  // M323-fix (2026-05-30): gắn với phiên L2 của NV. Mỗi lần NV đăng nhập (TOTP) ->
  // l2SessionId mới -> effect chạy lại -> popup hiện lại. (Trước đây gắn với
  // accessToken/role của máy kiosk -> không chạy lại khi NV mới đăng nhập.)
  const l2SessionId = useAuthStore(s => s.l2SessionId);
  const [ann, setAnn] = useState(null);
  const [visible, setVisible] = useState(false);
  const [remain, setRemain] = useState(0);
  const hideTimer = useRef(null);
  const tickTimer = useRef(null);

  useEffect(() => {
    // Chỉ hiện cho NV (kiosk) ĐÃ đăng nhập L2. Reset popup cũ khi đổi NV.
    setVisible(false);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    if (tickTimer.current) clearInterval(tickTimer.current);
    if (!accessToken || role !== 'kiosk' || !l2SessionId) return undefined;
    let cancelled = false;
    api.getAnnouncement()
      .then((a) => {
        if (cancelled || !a || !a.enabled) return;
        // Key theo (version + l2SessionId): mỗi lần NV đăng nhập hiện 1 lần,
        // nhưng tải lại trang trong cùng phiên L2 thì không hiện lại (đỡ phiền).
        const seenKey = 'ann_seen_' + (a.version || 'x') + '_' + l2SessionId;
        if (sessionStorage.getItem(seenKey)) return;
        sessionStorage.setItem(seenKey, '1');
        const secs = Math.max(5, Math.min(3600, Number(a.durationSeconds) || 240));
        setAnn(a);
        setRemain(secs);
        setVisible(true);
        hideTimer.current = setTimeout(() => setVisible(false), secs * 1000);
        tickTimer.current = setInterval(() => {
          setRemain((r) => (r <= 1 ? 0 : r - 1));
        }, 1000);
      })
      .catch(() => { /* im lặng — không có thông báo thì thôi */ });
    return () => {
      cancelled = true;
      if (hideTimer.current) clearTimeout(hideTimer.current);
      if (tickTimer.current) clearInterval(tickTimer.current);
    };
  }, [accessToken, role, l2SessionId]);

  function close() {
    setVisible(false);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    if (tickTimer.current) clearInterval(tickTimer.current);
  }

  if (!visible || !ann) return null;

  const mm = String(Math.floor(remain / 60)).padStart(1, '0');
  const ss = String(remain % 60).padStart(2, '0');

  return (
    <div
      className="fixed bottom-4 right-4 z-[60] w-[370px] max-w-[calc(100vw-2rem)]"
      data-testid="announcement-popup"
    >
      <div className="rounded-2xl border border-amber-200 bg-white shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2.5 flex items-center gap-2">
          <span className="text-lg">📢</span>
          <span className="text-white font-semibold text-sm flex-1 leading-snug">{ann.title}</span>
          <button
            type="button"
            onClick={close}
            data-testid="announcement-close"
            aria-label="Đóng"
            className="text-white/90 hover:text-white text-lg leading-none px-1"
          >×</button>
        </div>
        <div className="px-4 py-3">
          <div className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{ann.body}</div>
          <div className="mt-3 flex items-center justify-between">
            <span className="text-[11px] text-slate-400">Tự đóng sau {mm}:{ss}</span>
            <button
              type="button"
              onClick={close}
              className="text-xs font-medium px-3 py-1.5 rounded-lg bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100"
            >Đã hiểu</button>
          </div>
        </div>
      </div>
    </div>
  );
}
